// lib/tracking/client-track.ts
// Client-side beacon for the allowlisted events on POST /api/track (see
// app/api/track/route.ts ALLOWED_EVENTS). Mirrors the sendBeacon-with-fetch
// -fallback pattern from components/seo/WebVitalsReporter.tsx so first-party
// click intent (cta_click, outbound_click, affiliate_click) reaches GA4 even
// through ad blockers that target google-analytics.com directly.

import { derivePageContext } from "@/lib/tracking/page-context";

const TRACK_ENDPOINT = "/api/track";

export type TrackedEventName = "cta_click" | "outbound_click" | "affiliate_click";

export type TrackedEventParams = Record<string, string | number | boolean>;

function currentPageContext(): TrackedEventParams {
  const path = typeof window !== "undefined" ? window.location.pathname : null;
  const { page_path, page_type, slug } = derivePageContext(path);
  return { page_path, page_type, slug };
}

/** Raw page address for the first-party /api/track endpoint, which is the
 *  authority: it sanitizes page_location and page_referrer (lib/tracking/
 *  page-location-client.ts), derives page_hostname, and applies the GA4 length
 *  limits before anything reaches GA4. Kept to two property reads because this
 *  module is duplicated into every client chunk that imports it. The browser
 *  already sends this same URL to /api/track as the Referer header. */
function currentPageFields(): TrackedEventParams {
  try {
    return { page_location: location.href, page_referrer: document.referrer };
  } catch {
    return {};
  }
}

export function trackEvent(name: TrackedEventName, params: TrackedEventParams): void {
  // affiliate_click always carries page_path, page_type and slug (paths only);
  // affiliate_click and cta_click also carry the page address fields.
  const enriched: TrackedEventParams =
    name === "affiliate_click"
      ? { ...currentPageContext(), ...currentPageFields(), ...params }
      : name === "cta_click"
        ? { ...currentPageFields(), ...params }
        : params;
  const body = JSON.stringify({ name, params: enriched });

  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(TRACK_ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch(TRACK_ENDPOINT, { method: "POST", body, keepalive: true }).catch(() => {});
  } catch {
    // Fire-and-forget — a beacon failure must never affect the click/nav UX.
  }
}

/** Shared cta_click beacon for primary review/CTA outbound links (review
 *  hero/verdict CTA, sticky mobile CTA, mid-article hop CTA) — keeps the
 *  event name and param shape identical across all three call sites. */
export function trackCtaClick(params: {
  linkUrl: string;
  ctaPlacement: string;
  productSlug?: string;
}): void {
  trackEvent("cta_click", {
    link_url: params.linkUrl,
    cta_placement: params.ctaPlacement,
    ...(params.productSlug ? { product_slug: params.productSlug } : {}),
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  });
}
